import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import { SelectedStore } from '@/pages/Admin/Subscription';
import OptionBtn from '../UI/OptionBtn';
import { currencyFormat } from '@/utils/currencyFormatter';
import Btn from '../UI/Btn';
import {
   createBillingPending,
   createWalletChargeXenditBilling,
   fetchPaymentChannels,
   type PaymentChannel,
} from '@/api/billing';
import { QRCodeCanvas } from 'qrcode.react';
// import { createSubscription } from '@/api/subscription';
import { toast } from 'react-toastify';

const SubscribeStore = ({ data }: { data: SelectedStore }) => {
   const [isLoading, setIsLoading] = useState(false);
   const [selected, setSelected] = useState({
      id: '6',
      label: '6 Month',
   });
   // What SmartFi can actually take money with today. GCash is switched off
   // until Xendit approves it, so this is never assumed.
   const [channels, setChannels] = useState<PaymentChannel[]>([]);
   // QR Ph keeps the operator on this page while they scan.
   const [qrString, setQrString] = useState('');

   const pricing = [
      {
         id: '1',
         label: '1 Month',
         price: 150,
         save: '',
         remarks: '',
      },
      {
         id: '3',
         label: '3 Month',
         price: 400,
         save: '-11%',
         remarks: '',
      },
      {
         id: '6',
         label: '6 Month',
         price: 700,
         save: '-22%',
         remarks: 'Popular Choice',
      },
      {
         id: '12',
         label: '1 Year',
         price: 1300,
         save: '-28%',
         remarks: '',
      },
   ];

   const handleSelect = (id: string) => {
      setSelected({
         id: id,
         label: pricing.find((item) => item.id === id)?.label ?? '',
      });
   };

   const handlePay = async (channel: PaymentChannel) => {
      if (isLoading) return;

      if (selected.id === '') return;

      try {
         setIsLoading(true);
         // const response = await createSubscription(data.id, selected.id);
         // console.log(`response: ${response}`);

         // if (response.status === 200) {
         //    toast.success('Subscription created successfully');
         // }

         const response = await createBillingPending({
            storeId: data.id,
            subscribe: selected.id,
         });

         if (response.status === 200) {
            try {
               // Pass the freshly-created billingId so the server charges
               // the RIGHT billing row. Without this, renewals would
               // attach the charge to the user's original (1mo) billing
               // and the webhook would create a wrong-amount subscription.
               const xenditResponse = await createWalletChargeXenditBilling({
                  storeId: data.id,
                  amount: response.data.amount,
                  billingId: response.data.billingId,
                  channel: channel.code,
               });

               // QR Ph does not redirect anywhere — draw the code instead and
               // let the operator scan it with their own app.
               if (xenditResponse.qrString) {
                  setQrString(String(xenditResponse.qrString));
                  return;
               }

               const checkoutUrl =
                  xenditResponse.checkoutUrl ||
                  xenditResponse.actions?.desktop_web_checkout_url ||
                  xenditResponse.actions?.mobile_web_checkout_url;
               if (checkoutUrl) {
                  window.location.href = checkoutUrl;
               } else {
                  toast.error(
                     `Could not start the ${channel.label} payment. Try another method.`
                  );
               }
            } catch (xenditError) {
               console.error('Payment creation failed:', xenditError);
               // Handle Xendit payment creation error
               // You might want to show an error message to the user
               // and possibly delete the pending transaction
            }
         }

         // console.log(`response: ${response}`);
      } catch (err) {
         console.error('Billing creation failed:', err);
      } finally {
         setIsLoading(false);
      }
   };

   const selectedPrice =
      pricing.find((item) => item.id === selected.id)?.price ?? 0;

   useEffect(() => {
      let cancelled = false;
      fetchPaymentChannels(selectedPrice).then((list) => {
         if (!cancelled) setChannels(list);
      });
      return () => {
         cancelled = true;
      };
   }, [selectedPrice]);

   return (
      <Wrapper>
         <Pricing>
            {pricing.map((item) => (
               <Item
                  key={item.id}
                  $active={selected.id === item.id}
                  onClick={() => handleSelect(item.id)}
               >
                  <ItemMain>
                     <OptionBtn active={selected.id === item.id} />
                     <TitleBadge $pro={true}>PRO</TitleBadge>
                     <ItemLabel>{item.label}</ItemLabel>
                  </ItemMain>
                  <ItemPrice>
                     {item.save ? (
                        <ItemSave>SAVE {item.save} OFF</ItemSave>
                     ) : (
                        <Span />
                     )}
                     <ItemPriceValue>
                        {currencyFormat(item.price, true)}
                     </ItemPriceValue>
                  </ItemPrice>
               </Item>
            ))}
         </Pricing>
         <BtnWrapper>
            <ItemLabelSelectedWrapper>
               <ItemLabel>Selected: </ItemLabel>
               <ItemLabelSelected>{selected.label}</ItemLabelSelected>
            </ItemLabelSelectedWrapper>
            <PaymentAction>
               {qrString ? (
                  <QrBox>
                     <QrTitle>
                        Scan to pay {currencyFormat(selectedPrice, true)}
                     </QrTitle>
                     <QRCodeCanvas value={qrString} size={200} level="M" />
                     <QrHint>
                        Open your bank or wallet app, choose Scan QR, and scan
                        this code. Your subscription starts as soon as the
                        payment goes through.
                     </QrHint>
                  </QrBox>
               ) : channels.length === 0 ? (
                  <NoPayNote>
                     Online payment is unavailable right now. Ask SmartFi for
                     the bank or GCash details to pay by hand.
                  </NoPayNote>
               ) : (
                  channels.map((ch) => (
                     <PayBtn
                        key={ch.code}
                        onClick={() => handlePay(ch)}
                        $disabled={isLoading}
                     >
                        {ch.code === 'GCASH' && (
                           <PayBtnIcon src="/gcashpay.webp" />
                        )}
                        <PayBtnLabel>
                           Pay {currencyFormat(selectedPrice, true)} with{' '}
                           {ch.label}
                        </PayBtnLabel>
                     </PayBtn>
                  ))
               )}
               {/* <Btn
                  title={`Pay now ${
                     selected.id === ''
                        ? '0'
                        : currencyFormat(
                             pricing.find((item) => item.id === selected.id)
                                ?.price ?? 0,
                             true
                          )
                  }`}
                  type={selected.id === '' ? 'disabled' : 'normal'}
                  size="small"
                  bgcolor={colors.PRIMARY}
                  textcolor={colors.WHITE}
               /> */}
            </PaymentAction>
         </BtnWrapper>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   /* min-width: 500px; */
   height: 100%;
   background-color: ${colors.WHITE};

   @media (max-width: 768px) {
      width: 100%;
   }
`;

const Pricing = styled.div`
   margin-top: 1rem;
`;

const ItemMain = styled.div`
   display: flex;
   align-items: center;
   gap: 1rem;
`;

const ItemPrice = styled.div`
   display: grid;
   grid-template-columns: auto auto;
   align-items: center;
   gap: 1rem;
   @media (max-width: 768px) {
     display: flex;
     justify-content: space-between;
   }
   
`;

const ItemLabel = styled.p`
   font-size: 14px;
   font-weight: 600;
`;

const ItemLabelSelected = styled.p`
   font-size: 14px;
   font-weight: 600;
   color: ${colors.TEAL};
   width: 200px;
`;

const ItemLabelSelectedWrapper = styled.div`
   display: flex;
   align-items: center;
   gap: 0.5rem;
   flex-shrink: 0;
   white-space: nowrap;
`;

const ItemSave = styled.p`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.WHITE};
   background-color: ${colors.TEAL};
   padding: 0.2rem 0.5rem;
   border-radius: 4px;
`;

const ItemPriceValue = styled.p`
   font-size: 14px;
   font-weight: 600;
   color: ${colors.GRAY1};
`;
const Span = styled.span`
   width: 100%;
`;

const BtnWrapper = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   gap: 1rem;
   margin-top: 1rem;

   @media (max-width: 480px) {
      flex-direction: column;
      align-items: stretch;
      gap: 0.75rem;
   }
`;

const PaymentAction = styled.div`
   display: flex;
   justify-content: flex-end;
   align-items: center;
   justify-content: flex-end;
   gap: 0.5rem;
   flex-shrink: 0;
`;

const PaymentIcon = styled.img`
   width: 50px;
`;

const PayBtn = styled.div<{ $disabled: boolean }>`
   display: flex;
   align-items: center;
   gap: 0.5rem;
   font-size: 14px;
   font-weight: 600;
   color: ${colors.WHITE};
   background-color: ${colors.BLUE_LIGHT};
   padding: 10px 1rem;
   border-radius: 4px;
   cursor: pointer;
   opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
   pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

   /* @media (max-width: 768px) {
     width: 100%;
   } */
`;

const PayBtnIcon = styled.img`
   width: 20px;
`;

const PayBtnLabel = styled.p`
   font-size: 14px;
   font-weight: 600;
   color: ${colors.WHITE};
`;

const TitleBadge = styled.span<{
   $pro: boolean;
}>`
   font-size: 14px;
   border: 2px solid
      ${({ $pro }) => ($pro ? colors.PRIMARY50 : colors.SECONDARY)};
   border-radius: 4px;
   padding: 0.2rem 0.5rem;
   color: ${colors.PRIMARY};
   background: ${({ $pro }) => ($pro ? colors.PRIMARY20 : colors.SECONDARY)};
   font-weight: 600;
`;

const Item = styled.div<{
   $active: boolean;
}>`
   display: flex;
   justify-content: space-between;
   align-items: center;
   padding: 1rem;
   border: ${({ $active }) =>
      $active ? `1px solid ${colors.PRIMARY}` : `1px solid ${colors.GRAY14}`};
   border-radius: 8px;
   margin-bottom: 10px;
   cursor: pointer;
   background-color: ${({ $active }) =>
      $active ? 'var(--app-brand-tint)' : colors.WHITE};

   @media (max-width: 480px) {
      display: grid;
      grid-template-columns: 1fr;
      gap: 0.5rem;
      padding: 0.75rem;
   }
`;

export default SubscribeStore;

/* QR Ph — the operator stays on this screen while they scan. */
const QrBox = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 8px;
   padding: 6px 0;
`;

const QrTitle = styled.div`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
`;

const QrHint = styled.p`
   margin: 0;
   max-width: 280px;
   text-align: center;
   font-size: 12px;
   line-height: 1.5;
   color: ${colors.GRAY};
`;

const NoPayNote = styled.div`
   text-align: center;
   font-size: 13px;
   line-height: 1.5;
   color: ${colors.GRAY};
   padding: 8px 6px;
`;
